import { useCallback } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  ScreeningBatchCreatePayload,
  ScreeningBatchDetail,
  ScreeningBatchPageParams,
  ScreeningBatchReportRow,
  ScreeningBatchSummary,
} from '@/types';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

// Polls are deliberately unhurried, and both reads are conditional: a
// scheduled tick sends the last ETag as `If-None-Match` and the steady
// state is a body-less 304 (one indexed aggregate server-side). Under
// load an overlapping poll storm was a measurable contributor to API
// DB-pool pressure (2026-08-25); `usePoll` skips a tick while the previous
// fetch is still in flight and pauses hidden tabs.
export const LIST_POLL_MS = 30_000;
export const DETAIL_POLL_MS = 15_000;
/** Rows per page on the batch detail (the API caps a page at 200). */
export const DETAIL_PAGE_SIZE = 50;
/** The register export walks the rows in API-cap-sized pages. */
const EXPORT_PAGE_SIZE = 200;

const EMPTY_BATCHES: ScreeningBatchSummary[] = [];

/** Batch list with polling while any batch is running. */
export function useScreeningBatches() {
  const poll = usePoll<ScreeningBatchSummary[]>({
    interval: LIST_POLL_MS,
    queryFn: async ({ signal, etag, manual }) => {
      // A user-driven refetch is always a full read (ctx.etag is null for
      // manual fetches); scheduled ticks poll on the stored token.
      const result = await vnextApiClient.listScreeningBatches(
        {},
        { signal, etag: manual ? null : etag }
      );
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const { refetch } = poll;

  const createBatch = useCallback(
    async (payload: ScreeningBatchCreatePayload): Promise<ScreeningBatchSummary> => {
      // Deliberately no toast here: validation failures carry structured
      // row_errors the caller renders inline (ApiValidationError), same
      // pattern as the report-config import modal.
      const created = await vnextApiClient.createScreeningBatch(payload);
      await refetch();
      return created;
    },
    [refetch]
  );

  return {
    batches: poll.data ?? EMPTY_BATCHES,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch,
    createBatch,
  };
}

interface UseScreeningBatchResult {
  /** The header, whole-batch counts and the requested PAGE of rows. */
  batch: ScreeningBatchDetail | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  start: () => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  setWindow: (maxInFlight: number) => Promise<void>;
  /** Every row across all pages — for the register export. One-shot,
   * never polled. */
  fetchAllRows: () => Promise<ScreeningBatchReportRow[]>;
}

/** Poll only while the batch can still change (running, or paused with
 * in-flight reports draining). */
function isMoving(batch: ScreeningBatchDetail): boolean {
  return batch.status === 'running' || batch.status === 'pending' || batch.counts.in_flight > 0;
}

/** One batch's detail (one page of rows), polling while anything is still
 * moving. Changing the page restarts the poll on the new page. */
export function useScreeningBatch(
  batchId: string | undefined,
  {
    page = 1,
    pageSize = DETAIL_PAGE_SIZE,
    rating = '',
    hrj,
    search = '',
    status = '',
    sort = '',
  }: ScreeningBatchPageParams = {}
): UseScreeningBatchResult {
  const poll = usePoll<ScreeningBatchDetail>({
    enabled: batchId !== undefined,
    interval: (batch) => (batch && isMoving(batch) ? DETAIL_POLL_MS : null),
    // A different page (or rating filter) is a different token space;
    // usePoll drops the stored ETag on every dependency change.
    refetchDeps: [batchId, page, pageSize, rating, hrj, search, status, sort],
    queryFn: async ({ signal, etag, manual }) => {
      if (!batchId) {
        throw new Error('batchId is required');
      }
      const result = await vnextApiClient.getScreeningBatch(
        batchId,
        // The filter only travels when set, so an unfiltered read keeps
        // its exact request shape (and token space).
        {
          page,
          pageSize,
          ...(rating ? { rating } : {}),
          ...(hrj === undefined ? {} : { hrj }),
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(sort ? { sort } : {}),
        },
        { signal, etag: manual ? null : etag }
      );
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const { refetch } = poll;

  const runAction = useCallback(
    async (action: 'start' | 'pause' | 'resume') => {
      if (!batchId) {
        return;
      }
      try {
        if (action === 'start') {
          await vnextApiClient.startScreeningBatch(batchId);
        } else if (action === 'pause') {
          await vnextApiClient.pauseScreeningBatch(batchId);
        } else {
          await vnextApiClient.resumeScreeningBatch(batchId);
        }
        notifications.show({
          title: `Batch ${action}${action === 'pause' ? 'd' : 'ed'}`,
          message: 'Dispatch state updated.',
          color: 'green',
        });
        await refetch();
      } catch (err) {
        const e = toError(err);
        Sentry.captureException(e);
        notifications.show({
          title: `Could not ${action} batch`,
          message: e.message,
          color: 'red',
        });
      }
    },
    [batchId, refetch]
  );

  const setWindow = useCallback(
    async (maxInFlight: number) => {
      if (!batchId) {
        return;
      }
      try {
        await vnextApiClient.updateScreeningBatch(batchId, { max_in_flight: maxInFlight });
        notifications.show({
          title: 'Window updated',
          message: `Dispatch window is now ${maxInFlight} — applies at the next top-up.`,
          color: 'green',
        });
        await refetch();
      } catch (err) {
        const e = toError(err);
        Sentry.captureException(e);
        notifications.show({ title: 'Could not update window', message: e.message, color: 'red' });
      }
    },
    [batchId, refetch]
  );

  const fetchAllRows = useCallback(async (): Promise<ScreeningBatchReportRow[]> => {
    if (!batchId) {
      return [];
    }
    const rows: ScreeningBatchReportRow[] = [];
    for (let nextPage = 1; ; nextPage += 1) {
      const result = await vnextApiClient.getScreeningBatch(batchId, {
        page: nextPage,
        pageSize: EXPORT_PAGE_SIZE,
      });
      if (result === 'not-modified') {
        break; // no ETag was sent, so unreachable — keeps the union honest
      }
      rows.push(...result.data.reports);
      if (result.data.reports.length === 0 || rows.length >= result.data.reports_total) {
        break;
      }
    }
    return rows;
  }, [batchId]);

  return {
    batch: poll.data ?? null,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch,
    start: () => runAction('start'),
    pause: () => runAction('pause'),
    resume: () => runAction('resume'),
    setWindow,
    fetchAllRows,
  };
}
